import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { EnvelopeSimple, Plus, Trash, FloppyDisk, Bell, Robot, Clock } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ComplianceSettingsPage() {
  const [settings, setSettings] = useState({
    recipients: [],
    auto_report_enabled: true,
    auto_report_min_impact: 'alto',
    central_contracts_email: '',
    reminder_hours_unopened: 48,
    escalation_days_opened: 7,
    reminders_enabled: true,
  });
  const [newEmail, setNewEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/compliance/settings`, { credentials: 'include' });
      if (res.ok) setSettings(await res.json());
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const addEmail = () => {
    const e = newEmail.trim().toLowerCase();
    if (!e || !e.includes('@')) { toast.error('Email non valida'); return; }
    if (settings.recipients?.includes(e)) { toast.error('Email già presente'); return; }
    setSettings({ ...settings, recipients: [...(settings.recipients || []), e] });
    setNewEmail('');
  };

  const removeEmail = (e) => {
    setSettings({ ...settings, recipients: settings.recipients.filter(x => x !== e) });
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/compliance/settings`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          recipients: settings.recipients,
          auto_report_enabled: settings.auto_report_enabled,
          auto_report_min_impact: settings.auto_report_min_impact,
          central_contracts_email: settings.central_contracts_email,
          reminder_hours_unopened: settings.reminder_hours_unopened,
          escalation_days_opened: settings.escalation_days_opened,
          reminders_enabled: settings.reminders_enabled,
        }),
      });
      if (res.ok) toast.success('Impostazioni salvate');
      else toast.error('Errore salvataggio');
    } finally { setSaving(false); }
  };

  if (loading) return <div className="p-8 text-slate-500">Caricamento...</div>;

  return (
    <div className="p-6 max-w-3xl mx-auto" data-testid="compliance-settings-page">
      <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3 mb-2">
        <Bell size={32} weight="duotone" className="text-orange-500" />
        Impostazioni notifiche Compliance
      </h1>
      <p className="text-slate-500 mb-6">
        Quando il Compliance Watcher rileva una nuova delibera ARERA, può generare automaticamente
        una <strong>relazione PDF</strong>, indicizzarla nella <strong>Knowledge Base</strong> e
        inviarla via <strong>email</strong> ai destinatari qui sotto.
      </p>

      {/* AUTO TRIGGER */}
      <div className="bg-white border rounded-lg p-4 mb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Robot size={24} weight="duotone" className="text-orange-500" />
            <div>
              <div className="font-semibold">Generazione automatica relazione</div>
              <div className="text-xs text-slate-500">
                Cron settimanale lun 06:00 — genera report per delibere new + impatto selezionato
              </div>
            </div>
          </div>
          <Switch
            checked={settings.auto_report_enabled}
            onCheckedChange={(v) => setSettings({ ...settings, auto_report_enabled: v })}
            data-testid="auto-toggle"
          />
        </div>
        <div className="mt-3 pt-3 border-t flex items-center gap-2 text-sm">
          <span className="text-slate-600">Soglia minima:</span>
          {['alto', 'medio', 'basso'].map(lv => (
            <button key={lv} onClick={() => setSettings({ ...settings, auto_report_min_impact: lv })}
                    className={`px-3 py-1 rounded text-xs uppercase ${
                      settings.auto_report_min_impact === lv
                        ? 'bg-orange-500 text-white' : 'bg-slate-100 text-slate-600'
                    }`}
                    data-testid={`impact-${lv}`}>
              {lv}
            </button>
          ))}
        </div>
      </div>

      {/* RECIPIENTS */}
      <div className="bg-white border rounded-lg p-4 mb-4">
        <div className="flex items-center gap-2 mb-3">
          <EnvelopeSimple size={20} weight="duotone" className="text-orange-500" />
          <h2 className="font-semibold">Destinatari email relazioni</h2>
          <Badge variant="outline">{(settings.recipients || []).length}</Badge>
        </div>
        <div className="flex gap-2 mb-3">
          <Input value={newEmail} onChange={(e) => setNewEmail(e.target.value)}
                 placeholder="email@esempio.it"
                 onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addEmail())}
                 data-testid="new-email-input" />
          <Button onClick={addEmail} className="bg-orange-500 hover:bg-orange-600" data-testid="add-email-btn">
            <Plus size={14} className="mr-1" /> Aggiungi
          </Button>
        </div>
        {(settings.recipients || []).length === 0 ? (
          <div className="text-sm text-slate-400 italic py-4 text-center border-2 border-dashed rounded">
            Nessun destinatario — le relazioni auto-generate non verranno inviate via email
          </div>
        ) : (
          <div className="space-y-1">
            {settings.recipients.map(e => (
              <div key={e} className="flex items-center justify-between border rounded px-3 py-2 text-sm bg-slate-50">
                <span className="font-mono">{e}</span>
                <Button size="sm" variant="ghost" onClick={() => removeEmail(e)}
                        className="text-red-600 hover:bg-red-50" data-testid={`remove-${e}`}>
                  <Trash size={14} />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-white border rounded-lg p-4 mb-4">
        <div className="flex items-center gap-3 mb-3">
          <Clock size={24} weight="duotone" className="text-orange-500" />
          <div className="flex-1">
            <div className="font-semibold">Reminder automatici contratti (Sprint 5)</div>
            <div className="text-xs text-slate-500">Email di sollecito e escalation per contratti non firmati</div>
          </div>
          <Switch
            checked={settings.reminders_enabled}
            onCheckedChange={(v) => setSettings({ ...settings, reminders_enabled: v })}
            data-testid="reminders-toggle"
          />
        </div>
        {settings.reminders_enabled && (
          <div className="space-y-3 pt-3 border-t">
            <div>
              <label className="text-xs text-slate-500 block mb-1">
                Email centrale contratti (ricezione scansioni cartacee in CC)
              </label>
              <Input type="email"
                     value={settings.central_contracts_email}
                     onChange={(e) => setSettings({ ...settings, central_contracts_email: e.target.value })}
                     placeholder="contratti@tua-azienda.it"
                     data-testid="central-email-input" />
              <div className="text-[10px] text-slate-400 mt-1">
                Verrà messa in CC per le scansioni firmate (firma cartacea via reply-to).
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-slate-500 block mb-1">Reminder dopo N ore senza aperture</label>
                <Input type="number" min="1" max="720"
                       value={settings.reminder_hours_unopened}
                       onChange={(e) => setSettings({ ...settings, reminder_hours_unopened: parseInt(e.target.value || 48, 10) })}
                       data-testid="reminder-hours-input" />
                <div className="text-[10px] text-slate-400 mt-1">Default: 48h</div>
              </div>
              <div>
                <label className="text-xs text-slate-500 block mb-1">Escalation dopo N giorni (aperto, non firmato)</label>
                <Input type="number" min="1" max="90"
                       value={settings.escalation_days_opened}
                       onChange={(e) => setSettings({ ...settings, escalation_days_opened: parseInt(e.target.value || 7, 10) })}
                       data-testid="escalation-days-input" />
                <div className="text-[10px] text-slate-400 mt-1">Default: 7gg</div>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="flex justify-end gap-2">
        <Button onClick={save} disabled={saving}
                className="bg-orange-500 hover:bg-orange-600" data-testid="save-settings-btn">
          <FloppyDisk size={14} className="mr-1" /> {saving ? 'Salvataggio...' : 'Salva impostazioni'}
        </Button>
      </div>

      <div className="mt-6 text-xs text-slate-400 italic">
        💡 Le relazioni manuali si possono generare cliccando sull'alert nella pagina "Raccolte dati e richieste"
        e premendo "Genera relazione" — non necessitano di destinatari salvati qui (verranno comunque inviate se configurati).
      </div>
    </div>
  );
}
